import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ShieldAlert } from "lucide-react";
import { useT } from "@/i18n";
import {
  authClient,
  REDIRECT_URI,
  saveTokens,
  takeChallenge,
  takePostAuthPath,
} from "@/lib/auth-client";
import Logo from "@/components/layout/Logo";

export function AuthCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useT();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const code = params.get("code");
    const oauthError = params.get("error");
    if (oauthError) {
      setError(oauthError);
      return;
    }
    if (!code) {
      setError("missing_code");
      return;
    }
    const challenge = takeChallenge();
    if (!challenge) {
      setError("missing_challenge");
      return;
    }
    (async () => {
      try {
        const exchanged = await authClient.exchange(code, REDIRECT_URI, challenge.verifier);
        if (exchanged.err) {
          setError(String(exchanged.err));
          return;
        }
        saveTokens({
          access: exchanged.tokens.access,
          refresh: exchanged.tokens.refresh,
        });
        // Hard reload so AuthProvider re-runs verify with fresh tokens.
        window.location.replace(takePostAuthPath());
      } catch (e) {
        setError(e instanceof Error ? e.message : "unknown_error");
      }
    })();
  }, [params, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 to-brand-50 px-6">
      <div className="w-full max-w-sm rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-soft">
        <div className="mb-6 flex justify-center">
          <Logo />
        </div>
        {error ? (
          <>
            <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50 text-red-600">
              <ShieldAlert className="h-6 w-6" />
            </div>
            <h1 className="text-lg font-semibold text-slate-900">{t("auth.callback_error_title")}</h1>
            <p className="mt-2 text-sm text-slate-600">{t("auth.callback_error_body")}</p>
            <Link to="/login" replace className="btn-primary mt-6 inline-flex">
              {t("auth.signin_cta")}
            </Link>
          </>
        ) : (
          <>
            <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-700">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
            <h1 className="text-lg font-semibold text-slate-900">{t("auth.callback_working_title")}</h1>
            <p className="mt-2 text-sm text-slate-600">{t("auth.callback_working_body")}</p>
          </>
        )}
      </div>
    </div>
  );
}
